// Os nove dicionários, carregados UM DE CADA VEZ.
//
// ─────────────────────────────────────────────────────────────────────────
// O QUE ISTO CONSERTA (medido, não estimado)
// ─────────────────────────────────────────────────────────────────────────
// Este arquivo era nove `import` estáticos e um `Record<Lang, …>` literal.
// Import estático de dicionário é import que o bundler NÃO consegue separar:
// os nove entravam no chunk de entrada, e todo mundo baixava nove idiomas
// para usar um. No build medido antes desta mudança, a entrada tinha
// 7.097 KB raw / 1.964 KB gzip, e os locales respondiam por ~41% disso.
//
// O usuário que paga essa conta não é abstrato: é o brasileiro em 4G que abre
// o app e espera oito dicionários que ele nunca vai ler.
//
// ─────────────────────────────────────────────────────────────────────────
// A RESTRIÇÃO QUE NÃO PODE CAIR
// ─────────────────────────────────────────────────────────────────────────
// O LanguageProvider pinta o idioma persistido no PRIMEIRO FRAME, sem gate
// assíncrono. Trocar 730 KB por um flash de idioma errado no boot seria trocar
// um problema por outro pior — quem abre o app em polonês veria português por
// alguns frames, toda vez.
//
// A saída não é gate no React, é ORDEM no boot: `src/main.tsx` espera o
// dicionário do idioma de boot ANTES de `createRoot().render()`. O splash
// estático do index.html (que já cobre o parse do bundle) cobre também essa
// espera, e quando o React roda o primeiro frame o dicionário já está aqui,
// em memória, acessível de forma SÍNCRONA por `peekLocale`.
//
// Ou seja: `t()` continua síncrono e continua certo no frame 1. O que ficou
// assíncrono é o BOOT, que já era assíncrono (o bundle também é baixado).
//
// ─────────────────────────────────────────────────────────────────────────
// O FALLBACK EN — dormente, não morto
// ─────────────────────────────────────────────────────────────────────────
// `t()` cai no inglês quando a chave falta no idioma ativo. Medido: hoje isso
// cobre EXATAMENTE 229 chaves, TODAS `baby.*`, em es/fr/it/de/pl/bg — e zero
// chaves fora do módulo baby. O módulo está removido (CLAUDE.md §8) e suas
// rotas vivem atrás de `BABY_MODULE_ENABLED`, desligada em produção.
//
// Então em produção o fallback cobre zero chaves e carregar o EN inteiro só
// para ele seria pagar 91 KB gzip por nada. Mas a flag EXISTE: ligá-la sem o
// inglês em memória pintaria chave crua em seis idiomas. Por isso o inglês é
// carregado junto com o idioma de boot SE E SOMENTE SE a flag estiver ligada
// — o custo vai para quem liga a flag, não para o brasileiro em 4G.
//
// ─────────────────────────────────────────────────────────────────────────
// TESTES
// ─────────────────────────────────────────────────────────────────────────
// Teste que precisa dos nove dicionários de forma síncrona importa
// `./translations.all`, que faz os nove imports estáticos. Ele NÃO pode ser
// importado por código de app — seria reimportar tudo e desfazer o split em
// silêncio. `src/test/localeChunks.test.ts` reprova se isso acontecer.

import type { FitnessExperienceCopyByFamily, FitnessExperienceFamily, FitnessExperienceLocale } from './fitnessExperienceTypes';
import { BABY_MODULE_ENABLED } from "@/lib/featureFlags";
import { LANGUAGES, type Lang } from "./locale";

// Re-exportado porque 8 módulos já importam o tipo por este caminho.
export type { Lang };

export type Dict = Readonly<Record<string, string>>;

/** Editorial guidance travels inside the same lazy locale entrypoint as UI copy. */
export type FitnessGuidanceFamily = 'base' | 'extended' | 'upper' | 'lower' | 'cardio' | 'arm' | 'machine' | 'advanced';
export type FitnessGuidanceCopy = readonly [string, string, string, string, string];
export type FitnessGuidanceLocale = Readonly<Record<FitnessGuidanceFamily, Readonly<Record<string, FitnessGuidanceCopy>>>>;
const FITNESS_GUIDANCE_FAMILIES: readonly FitnessGuidanceFamily[] = [
  'base', 'extended', 'upper', 'lower', 'cardio', 'arm', 'machine', 'advanced',
];


/**
 * Um chunk por arquivo de locale. `eager: false` é o default e é o ponto
 * inteiro: o Rollup vê nove `import()` distintos e emite nove chunks, sem
 * precisar de `manualChunks` no vite.config.ts.
 *
 * O glob é literal de propósito — o Vite resolve `import.meta.glob` em tempo
 * de BUILD, analisando o texto. Caminho montado em variável não é analisável
 * e voltaria para o bundle inteiro.
 */
const loaders = import.meta.glob<{ default: Dict }>("./locales/*.ts");

/** O idioma cujo dicionário serve de rede quando a chave falta no ativo. */
export const FALLBACK_LANG: Lang = "EN";

const carregados = new Map<Lang, Dict>();
const emVoo = new Map<Lang, Promise<Dict>>();
const guiasCarregados = new Map<Lang, FitnessGuidanceLocale>();
const fitnessCopyCarregadas = new Map<Lang, FitnessExperienceLocale>();
const FITNESS_EXPERIENCE_FAMILIES = {
  guided: true, workout: true, replacement: true, preferences: true, progress: true,
} satisfies Record<FitnessExperienceFamily, true>;

/** All five same-language families resolve with the locale's existing import(). */
function readFitnessExperienceCopy(module: unknown, lang: Lang): FitnessExperienceLocale {
  const candidate = module && typeof module === 'object' && 'fitnessExperienceCopy' in module
    ? module.fitnessExperienceCopy : undefined;
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) throw new Error('[i18n] sem copy Fitness para ' + lang);
  const families = candidate as Record<string, unknown>;
  for (const family of Object.keys(FITNESS_EXPERIENCE_FAMILIES)) {
    const copy = families[family];
    if (!copy || typeof copy !== 'object' || Array.isArray(copy) || Object.keys(copy).length === 0
      || Object.values(copy).some(value => typeof value !== 'string' || !value.trim())) {
      throw new Error('[i18n] copy Fitness inválida para ' + lang + '/' + family);
    }
  }
  return candidate as FitnessExperienceLocale;
}

/** No import and no network side effect: boot/loadLocale owns readiness. */
export function peekFitnessExperienceCopy<F extends FitnessExperienceFamily>(lang: Lang, family: F): FitnessExperienceCopyByFamily[F] | undefined {
  return fitnessCopyCarregadas.get(lang)?.[family];
}

/** UI getters stay synchronous. A cold requested language may use loaded EN;
 * if neither is ready, the caller violated the existing locale boot boundary. */
export function requireFitnessExperienceCopy<F extends FitnessExperienceFamily>(lang: Lang, family: F): FitnessExperienceCopyByFamily[F] {
  const copy = peekFitnessExperienceCopy(lang, family) ?? peekFitnessExperienceCopy(FALLBACK_LANG, family);
  if (!copy) throw new Error('[i18n] loadLocale must finish before reading Fitness copy: ' + lang + '/' + family);
  return copy;
}

/** Validate the additional export without changing the default Dict contract
 * or importing any locale outside its own lazy entrypoint. */
function readFitnessGuidance(module: unknown, lang: Lang): FitnessGuidanceLocale {
  const candidate = module && typeof module === 'object' && 'fitnessGuidance' in module
    ? module.fitnessGuidance : undefined;
  if (!candidate || typeof candidate !== 'object') {
    throw new Error('[i18n] sem guias Fitness para ' + lang);
  }
  const families = candidate as Record<string, unknown>;
  for (const family of FITNESS_GUIDANCE_FAMILIES) {
    const copies = families[family];
    if (!copies || typeof copies !== 'object' || Array.isArray(copies)
      || Object.keys(copies).length === 0
      || Object.values(copies).some(copy => !Array.isArray(copy) || copy.length !== 5
        || copy.some(text => typeof text !== 'string' || !text.trim()))) {
      throw new Error('[i18n] guias Fitness inválidos para ' + lang + '/' + family);
    }
  }
  return candidate as FitnessGuidanceLocale;
}

/** Synchronous cache read only. Cold/unavailable languages return undefined;
 * loadLocale is the shared readiness boundary for UI text and all eight families. */
export function peekFitnessGuidance(
  lang: Lang,
  family: FitnessGuidanceFamily,
  key: string,
): FitnessGuidanceCopy | undefined {
  return guiasCarregados.get(lang)?.[family]?.[key];
}


function caminhoDe(lang: Lang): string {
  const def = LANGUAGES.find((d) => d.code === lang);
  if (!def) throw new Error(`[i18n] idioma fora de LANGUAGES: ${lang}`);
  return `./locales/${def.dictFile}.ts`;
}

/**
 * O dicionário JÁ carregado, ou `undefined`. SÍNCRONO de propósito: é isto que
 * o `t()` usa no primeiro frame. Se devolvesse Promise, o provider precisaria
 * de gate assíncrono e a restrição de cima cairia.
 */
export function peekLocale(lang: Lang): Dict | undefined {
  return carregados.get(lang);
}

/** Já está em memória? (usado por teste e pelo provider antes de trocar) */
export function isLocaleLoaded(lang: Lang): boolean {
  return carregados.has(lang);
}

/**
 * Carrega (uma vez) o dicionário de um idioma.
 *
 * Memoizado em DOIS mapas: o de carregados e o de promessas em voo. Sem o
 * segundo, duas chamadas concorrentes — boot + troca rápida de idioma —
 * disparariam dois `import()` do mesmo chunk.
 */
export function loadLocale(lang: Lang): Promise<Dict> {
  const pronto = carregados.get(lang);
  if (pronto) return Promise.resolve(pronto);

  const jaPedido = emVoo.get(lang);
  if (jaPedido) return jaPedido;

  const caminho = caminhoDe(lang);
  const loader = loaders[caminho];
  if (!loader) {
    // Só acontece se o arquivo sumir ou o dictFile estiver errado. Falhar alto
    // aqui é melhor que devolver {} e pintar chave crua na tela inteira.
    return Promise.reject(new Error(`[i18n] sem dicionário para ${lang} (${caminho})`));
  }

  const p = loader()
    .then((mod) => {
      const dict = mod.default;
      // The entrypoint import resolves only after its eight same-language
      // dependencies and five UI copy families. Validate before publishing any cache, so setLang
      // cannot commit a dictionary whose movement guidance is still absent.
      const guidance = readFitnessGuidance(mod, lang);
      const fitnessCopy = readFitnessExperienceCopy(mod, lang);
      fitnessCopyCarregadas.set(lang, fitnessCopy);
      guiasCarregados.set(lang, guidance);
      carregados.set(lang, dict);
      emVoo.delete(lang);
      return dict;
    })
    .catch((err) => {
      // Limpa a promessa em voo para a próxima tentativa poder acontecer: sem
      // isto, uma falha de rede no primeiro load deixaria o idioma quebrado
      // para sempre naquela sessão.
      emVoo.delete(lang);
      console.error(`[i18n] falha ao carregar o dicionário de ${lang}:`, err);
      throw err;
    });

  emVoo.set(lang, p);
  return p;
}

/** Carrega vários e resolve quando todos estiverem em memória. */
export async function loadLocales(langs: readonly Lang[]): Promise<void> {
  await Promise.all(langs.map((l) => loadLocale(l)));
}

/**
 * O que o BOOT precisa ter em memória antes do primeiro frame.
 *
 * O inglês entra na lista apenas com o módulo baby ligado — ver o bloco
 * "O FALLBACK EN" no topo. Com a flag desligada (produção) é UM chunk.
 */
export function bootLocales(lang: Lang): Lang[] {
  return BABY_MODULE_ENABLED && lang !== FALLBACK_LANG ? [lang, FALLBACK_LANG] : [lang];
}

/** Carrega o necessário para pintar `lang` no primeiro frame. */
export function loadBootLocales(lang: Lang): Promise<void> {
  return loadLocales(bootLocales(lang));
}
